import { type ReactElement } from 'react'
import { getName, type UserProfileData } from '@terros/common'
import { useDisclosure } from '@mantine/hooks'
import { Box, Card, Divider, Group, Menu, Stack, Text, UnstyledButton } from '@mantine/core'
import { useTranslation } from '@front/model'
import { ButtonSignOut } from '../signout'
import { MaterialSymbolIcon } from '../../terros/icon/MaterialSymbolIcon'
import { UserInfo } from '../../entityInfo'
import { useSidebarAccountActions } from './useSidebarAccountActions'
import { SidebarUserMenuItem } from './SidebarUserMenuItem'
import { AppearanceControl } from './AppearanceControl'

type DesktopAccountMenuProps = {
  user?: UserProfileData
  isOpen: boolean
}

export const DesktopAccountMenu = ({ user, isOpen }: DesktopAccountMenuProps): ReactElement => {
  const { t } = useTranslation()
  const [isMenuOpen, { open: openMenu, close: closeMenu }] = useDisclosure(false)
  const userName = user ? getName(user) : t('Account')

  return (
    <Menu
      closeOnItemClick={false}
      offset={12}
      position='right-end'
      shadow='lg'
      width={280}
      withArrow
      onClose={closeMenu}
      onOpen={openMenu}
    >
      <Menu.Target>
        <UnstyledButton className='w-full'>
          <Card
            bg={isMenuOpen ? 'var(--mantine-color-default-hover)' : undefined}
            p={isOpen ? 'sm' : 'xs'}
            radius='xl'
            withBorder
          >
            <Group gap='sm' justify={isOpen ? 'space-between' : 'center'} wrap='nowrap'>
              {user && <UserInfo avatarOnly linked={false} size='md' userId={user.userId} />}
              <Box className='min-w-0' flex={1} hidden={!isOpen}>
                <Text className='break-words' fw='bold'>
                  {userName}
                </Text>
                {user?.email && (
                  <Text c='dimmed' fz='sm' truncate>
                    {user.email}
                  </Text>
                )}
              </Box>
              <Box className='shrink-0' hidden={!isOpen}>
                <MaterialSymbolIcon icon={isMenuOpen ? 'expand_less' : 'expand_more'} size='lg' />
              </Box>
            </Group>
          </Card>
        </UnstyledButton>
      </Menu.Target>
      <DesktopAccountMenuDropdown user={user} onClose={closeMenu} />
    </Menu>
  )
}

const DesktopAccountMenuDropdown = ({
  user,
  onClose,
}: {
  user?: UserProfileData
  onClose: VoidFunction
}): ReactElement => {
  const { t } = useTranslation()
  const account = useSidebarAccountActions({ sidebarUser: user, onNavigate: onClose })

  return (
    <Menu.Dropdown p='md'>
      <Stack gap='md'>
        <Group align='flex-start' gap='sm' wrap='nowrap'>
          {account.user && <UserInfo avatarOnly linked={false} size='md' userId={account.user.userId} />}
          <Box className='min-w-0' flex={1}>
            <Text fw={700} lineClamp={1}>
              {account.userName}
            </Text>
            {account.user?.email && (
              <Text c='dimmed' fz='sm' lineClamp={1}>
                {account.user.email}
              </Text>
            )}
          </Box>
        </Group>
        <AppearanceControl />
        <Divider />
        <Stack gap='xs'>
          {account.user && <SidebarUserMenuItem icon='person' label={t('Profile')} onClick={account.openProfile} />}
          <SidebarUserMenuItem icon='settings' label={t('Settings')} onClick={account.openSettings} />
          <ButtonSignOut />
        </Stack>
      </Stack>
    </Menu.Dropdown>
  )
}
